import React from "react";

const rarityTint = (rarity = "") => {
    const r = String(rarity).toLowerCase();
    // Tweak to your taste — these are intentionally saturated but we use low opacity.
    if (r.startsWith("myth")) return "#9b5de5";      // Mythic: royal violet
    if (r.startsWith("legend")) return "#ffd54a";    // Legendary: bright gold
    if (r.startsWith("super super")) return "#ff6b6b"; // SSR: rose-gold
    if (r.startsWith("super")) return "#f59e0b";     // SR: amber
    if (r.startsWith("high")) return "#a855f7";      // HR: purple
    if (r === "rare") return "#3b82f6";              // Rare: blue
    if (r.startsWith("uncommon")) return "#22c55e";  // Uncommon: green
    if (r.startsWith("common")) return "#9aa0a6";    // Common: gray
    return null; // no tint fallback
};

export default function GoldenSpiralFrame({
    // visible border band thickness in viewBox units (500x700)
    band = 28,
    // outer padding from the very edge (also in viewBox units)
    outer = 8,
    cornerRadius = 28,
    stroke = 3,
    className,
    // NEW: rarity tint controls
    rarity,
    tintStrength = 0.22,   // 0..1; subtle by default
    tintMode = "overlay",  // "overlay" | "replace"
}) {
    const W = 500, H = 700;
    const inner = outer + band;
    const innerRadius = Math.max(0, cornerRadius - band);

    const tint = rarityTint(rarity) || "#000000";
    const useTint = Boolean(rarityTint(rarity));

    // In REPLACE mode we blend gold with the tint in the gradient stops.
    // In OVERLAY mode we keep the original gold gradient and add a tinted rect with opacity.
    const goldStops = [
        { o: "0%", c: "#B8860B" },
        { o: "25%", c: "#DAA520" },
        { o: "55%", c: "#FFD700" },
        { o: "80%", c: "#FFF4C1" },
        { o: "100%", c: "#CDA349" },
    ];

    // Helper to blend two hex colors (very simple linear blend in sRGB)
    const blendHex = (hexA, hexB, t) => {
        const h2i = (h) => parseInt(h, 16);
        const i2h = (n) => n.toString(16).padStart(2, "0");
        const pa = hexA.replace("#", ""); const pb = hexB.replace("#", "");
        const r = Math.round((1 - t) * h2i(pa.slice(0, 2)) + t * h2i(pb.slice(0, 2)));
        const g = Math.round((1 - t) * h2i(pa.slice(2, 4)) + t * h2i(pb.slice(2, 4)));
        const b = Math.round((1 - t) * h2i(pa.slice(4, 6)) + t * h2i(pb.slice(4, 6)));
        return `#${i2h(r)}${i2h(g)}${i2h(b)}`;
    };

    const replacedStops = goldStops.map(({ o, c }) => ({
        o,
        c: useTint ? blendHex(c, tint, 0.35) : c, // gentle blend for replace mode
    }));

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            aria-hidden
            className={className}
            style={{ display: "block", width: "100%", height: "100%" }}
        >
            <defs>
                {/* Base gold gradient (or replaced-blend) */}
                <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    {(tintMode === "replace" ? replacedStops : goldStops).map(({ o, c }) => (
                        <stop key={o} offset={o} stopColor={c} />
                    ))}
                </linearGradient>

                <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2.2" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>

                {/* Ring mask: show only the band between outer and inner rects */}
                <mask id="ringMask">
                    <rect x="0" y="0" width={W} height={H} fill="black" />
                    <rect
                        x={outer}
                        y={outer}
                        width={W - 2 * outer}
                        height={H - 2 * outer}
                        rx={cornerRadius}
                        ry={cornerRadius}
                        fill="white"
                    />
                    <rect
                        x={inner}
                        y={inner}
                        width={W - 2 * inner}
                        height={H - 2 * inner}
                        rx={innerRadius}
                        ry={innerRadius}
                        fill="black"
                    />
                </mask>

                {/* Subtle moving highlight */}
                <linearGradient id="shimmer" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                    <stop offset="40%" stopColor="rgba(255,255,255,0.6)" />
                    <stop offset="60%" stopColor="rgba(255,255,255,0)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
                <mask id="shimmerMask">
                    <rect width="100%" height="100%" fill="black" />
                    <rect width="40%" height="100%" fill="white">
                        <animate attributeName="x" from="-40%" to="100%" dur="5s" repeatCount="indefinite" />
                    </rect>
                </mask>
            </defs>

            {/* Fill the ring with (gold or replaced) gradient */}
            <g mask="url(#ringMask)" filter="url(#softGlow)">
                <rect x="0" y="0" width={W} height={H} fill="url(#goldGradient)" />
            </g>

            {/* OVERLAY tint: sits above base fill, below spirals */}
            {useTint && tintMode === "overlay" && (
                <g mask="url(#ringMask)" filter="url(#softGlow)" opacity={tintStrength}>
                    <rect x="0" y="0" width={W} height={H} fill={tint} />
                </g>
            )}

            {/* Spirals (always pure gold gradient so they read clearly) */}
            <g
                mask="url(#ringMask)"
                stroke="url(#goldGradient)"
                strokeWidth={stroke}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#softGlow)"
            >
                {/* Corner spirals */}
                <path d="
          M 42 60
          C 70 40, 108 40, 128 68
          C 145 92, 124 118, 100 120
          C 76 122, 60 106, 62 86
          C 64 68, 78 58, 96 60
        " />
                <path d="
          M 458 60
          C 430 40, 392 40, 372 68
          C 355 92, 376 118, 400 120
          C 424 122, 440 106, 438 86
          C 436 68, 422 58, 404 60
        " />
                <path d="
          M 42 640
          C 70 660, 108 660, 128 632
          C 145 608, 124 582, 100 580
          C 76 578, 60 594, 62 614
          C 64 632, 78 642, 96 640
        " />
                <path d="
          M 458 640
          C 430 660, 392 660, 372 632
          C 355 608, 376 582, 400 580
          C 424 578, 440 594, 438 614
          C 436 632, 422 642, 404 640
        " />

                {/* Side vines */}
                <path d="M 54 120 C 80 160, 80 220, 54 260 C 28 300, 28 360, 54 400 C 78 436, 90 468, 54 500" />
                <path d="M 446 120 C 420 160, 420 220, 446 260 C 472 300, 472 360, 446 400 C 422 436, 410 468, 446 500" />

                {/* Top & bottom waves */}
                <path d="M 120 36 C 180 20, 320 20, 380 36" />
                <path d="M 120 664 C 180 680, 320 680, 380 664" />
            </g>

            {/* Shimmer over the ring only */}
            <g mask="url(#ringMask)" opacity="0.35">
                <rect x="0" y="0" width={W} height={H} fill="url(#shimmer)" mask="url(#shimmerMask)" />
            </g>
        </svg>
    );
}
